import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Instagram } from "lucide-react";
import { SHOWS, EMAIL, INSTAGRAM } from "../../data/content";
import Footer from "./Footer";
import { setCanonical, setMetaDescription } from "./seo";

export default function ShowPage() {
  const { slug } = useParams();
  const show = SHOWS.find((s) => s.id === slug);

  useEffect(() => {
    if (!show) return;
    document.title = `${show.title} · Karamiko · Teatre infantil a Lleida`;
    setCanonical(`https://karamiko.cat/espectacles/${show.id}`);
    setMetaDescription(show.description.slice(0, 160));
  }, [show]);

  if (!show) return <Navigate to="/" replace />;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 bg-kraft/90 backdrop-blur-md paper-shadow">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <Link to="/" className="hover-wiggle" aria-label="Karamiko inici" data-testid="showpage-logo">
            <img src="/logo-wordmark.png" alt="Karamiko" className="h-10 w-auto sm:h-12" />
          </Link>
          <Link
            to="/#espectacles"
            className="inline-flex items-center gap-2 font-body text-sm font-bold text-forest link-draw"
            data-testid="showpage-back"
          >
            <ArrowLeft className="h-4 w-4" /> Tots els espectacles
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pb-20 pt-28">
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative collage tone-white p-6 sm:p-10"
          data-testid={`showpage-${show.id}`}
        >
          <div className="grid gap-8 md:grid-cols-2 md:items-center">
            <div className="relative overflow-hidden bg-kraft">
              <img src={show.image} alt={`Escena de l'espectacle ${show.title} de Karamiko`} className="h-full w-full object-cover" />
              <span className="absolute left-3 top-3 rounded-full bg-forest/90 px-3 py-1 font-body text-xs font-bold text-paper">
                {show.age}
              </span>
            </div>
            <div>
              <span className="font-hand text-xl font-bold text-sage-ink">{show.tag}</span>
              <h1 className="mt-1 font-display text-4xl font-bold lowercase text-forest sm:text-5xl">{show.title}</h1>
              <p className="mt-4 font-body leading-relaxed text-forest/80">{show.description}</p>
              <ul className="mt-5 flex flex-wrap gap-2">
                {show.bullets.map((b) => (
                  <li key={b} className="rounded-full bg-sage/12 px-3 py-1 font-body text-xs font-semibold text-forest">
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-kraft-dark pt-7 text-center">
            <p className="font-display text-xl font-semibold text-forest">Voleu programar {show.title} a la vostra escola o biblioteca?</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              <a
                href={`mailto:${EMAIL}?subject=Programació de ${show.title}`}
                className="inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3 font-display font-semibold text-paper transition-colors hover:bg-terracotta"
                data-testid="showpage-email"
              >
                <Mail className="h-5 w-5" /> {EMAIL}
              </a>
              <a
                href={INSTAGRAM.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-body font-semibold text-forest link-draw"
                data-testid="showpage-instagram"
              >
                <Instagram className="h-4 w-4 text-sage" /> {INSTAGRAM.handle}
              </a>
            </div>
          </div>
        </motion.article>
      </main>

      <Footer />
    </>
  );
}
